import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, LGBDatepicker } from "@components";
import { useDriverTimeEntries } from "../../hooks/useDriverTimeEntries";
import {
  overlappingTimeEntries,
  timeEntryDuration,
  timeEntryStamp,
  timeEntryWeek,
  todayInOslo,
} from "./timeEntries";

export function DriverTimeEntries({
  workspaceId,
  driverId,
}: {
  workspaceId: string;
  driverId: string;
}) {
  const { t } = useTranslation("driver");
  const [date, setDate] = useState(todayInOslo);
  const week = timeEntryWeek(date);
  const query = useDriverTimeEntries(workspaceId, driverId, week.from, week.to);
  const rows = query.isError ? [] : (query.data?.entries ?? []);
  const overlaps = overlappingTimeEntries(rows);
  const total = rows.reduce(
    (sum, row) => sum + (row.voided ? 0 : row.periodSeconds),
    0,
  );
  return (
    <div className="space-y-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-64">
          <label
            htmlFor="driver-time-week"
            className="mb-2 block text-sm font-medium text-text"
          >
            {t("profile.choose_week")}
          </label>
          <LGBDatepicker
            inputId="driver-time-week"
            selectedDate={date}
            select={setDate}
            allowPastDates
            maxDate={null}
            overlayCalendar
          />
        </div>
        <Button
          variant="secondary"
          onClick={() => query.refetch()}
          disabled={query.isFetching}
        >
          {t("profile.refresh")}
        </Button>
      </div>
      <p className="text-sm text-secondary">{week.label} · Europe/Oslo</p>
      {query.isLoading ? (
        <p role="status">{t("profile.loading")}</p>
      ) : query.isError ? (
        <p role="alert" className="text-danger">
          {t("profile.time_error")}
        </p>
      ) : (
        <>
          <div className="text-sm">
            <span className="font-semibold text-text">
              {t("profile.total")}: {timeEntryDuration(total)}
            </span>
            <p className="mt-1 text-muted">{t("profile.total_hint")}</p>
          </div>
          {overlaps.size > 0 && (
            <p className="text-sm text-warning" role="status">
              {t("profile.overlap_hint")}
            </p>
          )}
          {!rows.length ? (
            <p className="text-sm text-muted">
              {t(
                query.data?.accountLinked === false
                  ? "profile.no_account"
                  : "profile.no_entries",
              )}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-border text-secondary">
                  <tr>
                    {[
                      "recorded",
                      "reported",
                      "break",
                      "duration",
                      "comment",
                    ].map((key) => (
                      <th
                        key={key}
                        scope="col"
                        className="px-3 py-3 font-medium"
                      >
                        {t(`profile.${key}`)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr
                      key={row.id}
                      className="border-b border-border align-top"
                    >
                      <td className="whitespace-nowrap px-3 py-3">
                        {row.recordedStart ? (
                          <>
                            {timeEntryStamp(row.recordedStart)}
                            <br />
                            {row.recordedEnd
                              ? timeEntryStamp(row.recordedEnd)
                              : t("profile.active")}
                          </>
                        ) : (
                          t("profile.manual")
                        )}
                        {row.voided && (
                          <div className="font-medium text-muted">
                            {t("profile.voided")}
                          </div>
                        )}
                        {overlaps.has(row.id) && (
                          <div className="text-warning">
                            {t("profile.overlap")}
                          </div>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-3">
                        {row.reported ? (
                          <>
                            {timeEntryStamp(row.reported.start)}
                            <br />
                            {timeEntryStamp(row.reported.end)}
                          </>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-3 py-3">
                        {row.reported
                          ? t("profile.minutes", {
                              count: row.reported.breakMinutes,
                            })
                          : "—"}
                      </td>
                      <td className="px-3 py-3">
                        {timeEntryDuration(row.voided ? 0 : row.periodSeconds)}
                      </td>
                      <td className="min-w-[12rem] whitespace-pre-wrap break-words px-3 py-3">
                        {row.reported?.comment || (!row.voided && "—")}
                        {row.voided && (
                          <p className="mt-1 text-muted">
                            {t("profile.voided")}: {row.voidReason}
                          </p>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
